import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ChevronRight, Package } from "lucide-react";
import { shopApi, TOKEN_KEY } from "@/lib/shopApi";
import { money, formatApiErrorDetail } from "@/lib/api";
import { useMember } from "@/context/MemberContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { fieldCls } from "@/components/store/AuthShell";
import { OrderStatus } from "@/pages/store/OrderDetail";

const saveBtn = "h-11 rounded-lg bg-[#89CFF0] px-6 font-semibold text-slate-900 transition-colors hover:bg-[#74C2EC] disabled:opacity-60";
const card = "space-y-4 rounded-xl border border-slate-200 bg-white p-5 sm:p-6";

function useSave() {
  const [busy, setBusy] = useState(false);
  const run = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); return true; }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); return false; }
    finally { setBusy(false); }
  };
  return [busy, run];
}

function OrdersTab() {
  const [orders, setOrders] = useState(null);
  useEffect(() => { shopApi.get("/shop/orders").then((r) => setOrders(r.data)).catch(() => {}); }, []);
  if (!orders) return <div className="text-slate-500">Loading…</div>;
  if (!orders.length) return <div className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center text-slate-500" data-testid="account-no-orders"><Package className="mx-auto mb-3 h-6 w-6 text-sky-600" />No orders yet. <Link to="/shop" className="font-semibold text-sky-700">Start shopping</Link></div>;
  return (
    <div className="divide-y divide-slate-100 overflow-hidden rounded-xl border border-slate-200 bg-white" data-testid="account-orders-list">
      {orders.map((o) => (
        <Link key={o.id} to={`/account/orders/${o.id}`} className="flex items-center justify-between gap-3 p-4 transition-colors hover:bg-slate-50 sm:p-5" data-testid={`account-order-${o.id}`}>
          <div><div className="font-mono font-bold text-slate-900">{o.order_number}</div><div className="text-sm text-slate-500">{new Date(o.created_at).toLocaleDateString("en-AU")} · {o.items.length} item{o.items.length === 1 ? "" : "s"}</div></div>
          <div className="flex flex-wrap items-center justify-end gap-2 sm:gap-3"><OrderStatus status={o.status} /><span className="font-semibold" data-testid={`account-order-total-${o.id}`}>{money(o.total)}</span><ChevronRight className="h-4 w-4 text-slate-400" /></div>
        </Link>
      ))}
    </div>
  );
}

function ProfileTab() {
  const { member, setMember } = useMember();
  const [f, setF] = useState({ name: member.name, email: member.email });
  const [busy, run] = useSave();
  const submit = async (e) => {
    e.preventDefault();
    let pending = false;
    const ok = await run(async () => { const { data } = await shopApi.put("/shop/account/profile", f); pending = data.email_pending; setMember(data); }, "Account details updated");
    if (ok && pending) toast.info(`We sent a confirmation link to ${f.email}. Your email changes once you click it.`);
  };
  return (
    <form onSubmit={submit} className={card} data-testid="account-profile-form">
      <div className="space-y-1.5"><Label>Full name</Label><Input required minLength={2} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={fieldCls} data-testid="profile-name-input" /></div>
      <div className="space-y-1.5"><Label>Email address</Label><Input type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={fieldCls} data-testid="profile-email-input" /></div>
      {member.pending_email && <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800" data-testid="profile-pending-email">Waiting for you to confirm {member.pending_email} — check that inbox for the link.</p>}
      <button disabled={busy} className={saveBtn} data-testid="profile-save-btn">{busy ? "Saving…" : "Save details"}</button>
    </form>
  );
}

function DeliveryTab() {
  const { member, setMember } = useMember();
  const [f, setF] = useState(member.delivery || { name: "", phone: "", address: "" });
  const [busy, run] = useSave();
  const submit = (e) => { e.preventDefault(); run(async () => setMember((await shopApi.put("/shop/account/delivery", f)).data), "Delivery information saved"); };
  return (
    <form onSubmit={submit} className={card} data-testid="account-delivery-form">
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5"><Label>Recipient name</Label><Input required minLength={2} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={fieldCls} data-testid="delivery-name-input" /></div>
        <div className="space-y-1.5"><Label>Phone</Label><Input required minLength={6} value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className={fieldCls} data-testid="delivery-phone-input" /></div>
      </div>
      <div className="space-y-1.5"><Label>Delivery address</Label><Textarea rows={3} value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} className="rounded-lg border-slate-200" placeholder="Street, suburb, state, postcode" data-testid="delivery-address-input" /></div>
      <button disabled={busy} className={saveBtn} data-testid="delivery-save-btn">{busy ? "Saving…" : "Save delivery info"}</button>
    </form>
  );
}

function PasswordTab() {
  const [f, setF] = useState({ current_password: "", new_password: "", confirm: "" });
  const [busy, run] = useSave();
  const submit = async (e) => {
    e.preventDefault();
    if (f.new_password.length < 8) return toast.error("New password must be at least 8 characters");
    if (f.new_password !== f.confirm) return toast.error("Passwords do not match");
    if (await run(async () => { const { data } = await shopApi.put("/shop/account/password", { current_password: f.current_password, new_password: f.new_password }); sessionStorage.setItem(TOKEN_KEY, data.token); }, "Password changed"))
      setF({ current_password: "", new_password: "", confirm: "" });
  };
  return (
    <form onSubmit={submit} className={card} data-testid="account-password-form">
      <div className="space-y-1.5"><Label>Current password</Label><Input type="password" required value={f.current_password} onChange={(e) => setF({ ...f, current_password: e.target.value })} className={fieldCls} data-testid="password-current-input" /></div>
      <div className="space-y-1.5"><Label>New password</Label><Input type="password" required value={f.new_password} onChange={(e) => setF({ ...f, new_password: e.target.value })} className={fieldCls} data-testid="password-new-input" /></div>
      <div className="space-y-1.5"><Label>Confirm new password</Label><Input type="password" required value={f.confirm} onChange={(e) => setF({ ...f, confirm: e.target.value })} className={fieldCls} data-testid="password-confirm-input" /></div>
      <button disabled={busy} className={saveBtn} data-testid="password-save-btn">{busy ? "Saving…" : "Change password"}</button>
    </form>
  );
}

export default function Account() {
  const { member } = useMember();
  const trig = "rounded-lg px-4 py-2 text-sm font-medium data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm";
  return (
    <div data-testid="account-page">
      <div className="text-xs font-semibold uppercase tracking-wider text-sky-700">My account</div>
      <h1 className="mt-1 font-head text-4xl font-extrabold tracking-tight" data-testid="account-greeting">Hi, {member.name.split(" ")[0]}</h1>
      <p className="mt-1 text-sm text-slate-500">{member.email}</p>
      <Tabs defaultValue="orders" className="mt-8 max-w-3xl">
        <TabsList className="h-auto w-full justify-start gap-1 overflow-x-auto rounded-xl bg-slate-100 p-1 sm:w-auto">
          <TabsTrigger value="orders" className={trig} data-testid="account-tab-orders">Orders</TabsTrigger>
          <TabsTrigger value="profile" className={trig} data-testid="account-tab-profile">Details</TabsTrigger>
          <TabsTrigger value="delivery" className={trig} data-testid="account-tab-delivery">Delivery</TabsTrigger>
          <TabsTrigger value="password" className={trig} data-testid="account-tab-password">Password</TabsTrigger>
        </TabsList>
        <TabsContent value="orders" className="mt-6"><OrdersTab /></TabsContent>
        <TabsContent value="profile" className="mt-6"><ProfileTab /></TabsContent>
        <TabsContent value="delivery" className="mt-6"><DeliveryTab /></TabsContent>
        <TabsContent value="password" className="mt-6"><PasswordTab /></TabsContent>
      </Tabs>
    </div>
  );
}
